import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("ad_token"));
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("ad_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  const refreshMe = useCallback(async () => {
    if (!token) return null;
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      localStorage.setItem("ad_user", JSON.stringify(res.data));
      return res.data;
    } catch (e) {
      // token invalid
      logout();
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      setToken(res.data.token);
      setUser(res.data.user);
      localStorage.setItem("ad_token", res.data.token);
      localStorage.setItem("ad_user", JSON.stringify(res.data.user));
      return { ok: true, user: res.data.user };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Login failed" };
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, payload);
      setToken(res.data.token);
      setUser(res.data.user);
      localStorage.setItem("ad_token", res.data.token);
      localStorage.setItem("ad_user", JSON.stringify(res.data.user));
      return { ok: true, user: res.data.user };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Registration failed" };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("ad_token");
    localStorage.removeItem("ad_user");
  };

  const value = { token, user, loading, login, register, logout, refreshMe, setUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
